import React, { Fragment, useState } from "react";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { KPI } from "../../components/KPI.js";
import UntaggedResourceTable from "../../components/UntaggedResourceTable.jsx";
import _ from "underscore";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "../../tailwind.config.js";
import { useQuery } from "@tanstack/react-query";
import { Dropdown } from "../../components/form-components/Dropdown.jsx";
import { useKeycloak } from "@react-keycloak/web";

const API_URL = process.env.REACT_APP_API_URL;
const colors = resolveConfig(tailwindConfig).theme.colors;

const UntaggedResources = () => {
	const [openRows, ] = useState([]);
	const [tagKeys, setTagKeys] = useState([]);
	const [selectedTagKey, setSelectedTagKey] = useState({label: "All tags", value: "All tags"});
	const [resourceList, setResourceList] = useState([]);
	const [resourceKPIs, setResourceKPIs] = useState({
		Compute: 0,
		Storage: 0,
		Database: 0,
		Networking: 0,
	});
	const { keycloak } = useKeycloak();


	function toggleRow(resourceType) {
		const openResourceTypes = sessionStorage.getItem("openResourceTypes");

		let openRows = openResourceTypes ? openResourceTypes.split(",") : [];

		const index = openRows.indexOf(String(resourceType));

		if (index > -1) {
			openRows = openRows.filter((row) => row !== String(resourceType));
		} else {
			openRows = [...openRows, String(resourceType)];
		}

		sessionStorage.setItem("openResourceTypes", openRows.join(","));
	}

	function buildRequest() {
		let reqFilters = [];

		if (selectedTagKey.value !== "All tags") {
			// the format of the filter is different when the field is tag key
			// the operator is either is null or is not null
			// the values are the tag key that should be used to filter on (empty of filled)
			reqFilters.push({
				field: "tag key",
				values: [selectedTagKey.value],
				operator: "is null",
				is_subgroup: true,
			});
		} else {				
			reqFilters.push({
				field: "tags",
				operator: "=",
				values: [""],
				is_subgroup: true,
			});
		}

		return {
			filters: reqFilters,
		};
	}

	function getRequestOptions() {
		if (keycloak.token === "") return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + keycloak.token);

		let method = "POST";

		const requestBody = buildRequest();

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		return requestOptions;
	}

	// function retrieveResourceList2() {
	// 	if (keycloak.token === "") return;

	// 	const openResourceTypes = sessionStorage.getItem("openResourceTypes");

	// 	fetch(API_URL + "/get-resource-list", getRequestOptions())
	// 		.then((response) => {
	// 			if (response.status !== "200") {
	// 			}
	// 			return response.json();
	// 		})
	// 		.then((json) => {
	// 			setResourceList(prepareData(json.ResourceList));
	// 		});
	// }

	// useEffect(() => {
	// 	retrieveResourceList();
	// }, [keycloak.token, location]);

	const retrieveResourceList = async(token, selectedTagKey) => {
		if (token === "" || selectedTagKey === null) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token)

		const response = await fetch(API_URL + "/get-resource-list", getRequestOptions());

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	};

	useQuery(
		["resourceList", selectedTagKey],
		() => retrieveResourceList(keycloak.token, selectedTagKey),
		{
			onSuccess: (data) => {
				setResourceList(prepareData(data.ResourceList));
			},
			enabled:
				keycloak.token !== "" &&
				selectedTagKey !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);	

	/**
	 * Prepares resource data by grouping it hierarchically
	 * 
	 * @param {Array} data - Raw resource data array
	 * @returns {Array} Hierarchically grouped resource list with:
	 *   - Resources grouped first by resource_group
	 *   - "Other" resources grouped separately by resource_type
	 *   - "Other" group appended at the end
	 */
	function prepareData(data) {
		var resourceList = groupResourceList(data, "resource_cluster");
		var otherList = resourceList.filter(resource => resource.resource_cluster === "Other");
		var otherResourceList = groupResourceList(otherList[0].subRows, "resource_type");
		var groupedList = resourceList.filter(resource => resource.resource_cluster !== "Other");
		
		groupedList.push({
			resource_cluster: "Other",
			units: otherList[0].units,
			subRows: otherResourceList
		});

		return groupedList;
	}

	const retrieveResourceKPIs = async(token, selectedTagKey) => {
		if (token === "" || selectedTagKey === null) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token)

		let method = "POST";

		const requestBody = buildRequest();

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		const response = await fetch(API_URL + "/get-resource-kpis", requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	}

	useQuery(
		["resourceKPIs", selectedTagKey],
		() => retrieveResourceKPIs(keycloak.token, selectedTagKey),
		{
			onSuccess: (data) => {
				var kpis = {};
				var total = 0;
				var totalUntagged = 0;

				data.ResourceKPIs.forEach((kpi) => {
					kpis["PercentageTagged" + kpi.resource_cluster] = ((kpi.total_resources - kpi.untagged_resources) / kpi.total_resources) * 100;
					total += kpi.total_resources;
					totalUntagged += kpi.untagged_resources;
				});

				kpis.TotalResources = total;
				kpis.TotalUntaggedResources = totalUntagged;
				kpis.PercentageTaggedTotal = ((total - totalUntagged) / total) * 100;

				setResourceKPIs(kpis);
			},
			enabled: keycloak.token !== "",
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	function groupResourceList(data, groupBy) {
        // make sure the data is sorted and grouped by service_type
        const groupedAssets = [];
        let lastGroupBy = "";
        let lastGroup = {};

        let pdata = _.clone(data);

        pdata.sort((a, b) => {
            // Special case: if either is "Other", handle differently
            if (a[groupBy] === "Other") return 1;  // Other goes last
            if (b[groupBy] === "Other") return -1; // Other goes last
            
            // Normal alphabetical comparison for non-Other groups
            if (a[groupBy] < b[groupBy]) {
                return -1;
            }
            if (a[groupBy] > b[groupBy]) {
                return 1;
            }
            return 0;
        });

        pdata.forEach((asset) => {
            if (asset[groupBy] !== lastGroupBy) {
                lastGroupBy = asset[groupBy];
                lastGroup = {
                    [groupBy]: asset[groupBy],
                    units: 0,
                    subRows: []
                };
                groupedAssets.push(lastGroup);
            }

            lastGroup.units += asset.units;
            lastGroup.subRows.push(asset);
        });

        return groupedAssets;
    }

	// function processResourceList(data) {
	// 	const nodeMap = data.reduce((acc, item) => {
	// 		acc[item.resource_id] = { ...item, subRows: [] };
	// 		return acc;
	// 	}, {});

	// 	const buildTree = (data) => {
	// 		const result = [];
	// 		data.forEach((item) => {
	// 			if (item.parent_resource_id === -1) {
	// 				result.push(nodeMap[item.resource_id]);
	// 			} else {
	// 				const parent = nodeMap[item.parent_resource_id];
	// 				if (parent) {
	// 					parent.subRows.push(nodeMap[item.resource_id]);
	// 				}
	// 			}
	// 		});
	// 		return result;
	// 	};

    //     return buildTree(data);
	// }

	// function getTotalUnits(data) {
	// 	let total = 0;

	// 	if (data) {
	// 		data.forEach((asset) => {
	// 			total += asset.units;
	// 		});
	// 	}

	// 	return total;
	// }

	// const openDetails = (resourceID, type) => {
	// 	if (type === "Resource") {
	// 		navigate("/resource/" + resourceID);
	// 	}
	// }

	const retrieveTagKeys = async(token) => {
		if (token === "" ) return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token);

		let method = "GET";

		let requestOptions = {
			method: method,
			headers: headers,
		};

		const response = await fetch(API_URL + "/get-tag-keys", requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	}

	useQuery(
		["tagKeys"],
		() => retrieveTagKeys(keycloak.token),
		{
			onSuccess: (data) => {
				var tmpData = [];
				tmpData.push({
					label: "All tags",
					value: "All tags"
				});
				
                if (data.TagKeys !== undefined) {
                    data.TagKeys.forEach((element) => {
                        var tmpElement = {
                            label: element.tag_key,    
                            value: element.tag_key
                        };
                        tmpData.push(tmpElement);
                    });
                }
				// Sort tmpData by label, keeping "All tags" first
				tmpData.sort((a, b) => {
					if (a.value === "All tags") return -1;
					if (b.value === "All tags") return 1;
					return a.label.localeCompare(b.label);
				});

				setTagKeys(tmpData);
			},
			enabled: keycloak.token !== "",
			staleTime: 5000
		}
	);

	return (
		<Fragment>
			<AdministrationHeader
				title="Tagging Policy"
				subTitle="Define and manage the tags that are used in the different cloud environments"
				className="text-text-3"
				icon={icon({name: "tags", style: "light"})}
			/>
			<div className="pl-6 text-left flex text-text-2 mt-6">
				<div className="ml-4 pt-1">
					<div>
						<div className="">
							<Dropdown
                                valuePrefix="Tag category : "
								options={tagKeys}
								dropdownWidth={400}
								selectedValue={selectedTagKey}
								setSelectedValue={setSelectedTagKey}
                                backgroundColor={colors.pink6}
                                borderColor={colors.pink3}
                                textColor={colors.white}
							/>
						</div>
					</div>
				</div>

				{/* <FilterBar
					filters={filters}
					setFilters={setFilters}
				/> */}
			</div>
			<div className="text-left flex flex-auto flex-col ml-10 mt-4 border-solid border-border">
				<div className="flex flex-row gap-4 flex-wrap">
					<div className="flex-shrink-0">
						<KPI
							title="Untagged Resources"
							currentValue={resourceKPIs.TotalUntaggedResources}
							// previousValue={totalUnits}
							format="decimal"
							// showChange={true}
						/>
					</div>

					<div className="flex-shrink-0">
						<KPI
							title="Tagged Compute Resources"
							currentValue={resourceKPIs.PercentageTaggedCompute}
							// previousValue={resourceKPIs.filter(kpi => kpi.resource_group === "Compute").untagged_resources}
							format="percentage"
							// showChange={true}
						/>
					</div>

					<div className="flex-shrink-0">
						<KPI
							title="Tagged Storage Resources"
							currentValue={resourceKPIs.PercentageTaggedStorage}
							// previousValue={resourceKPIs.filter(kpi => kpi.resource_group === "Storage").untagged_resources}
							format="percentage"
							// showChange={true}
						/>
					</div>

					<div className="flex-shrink-0">
						<KPI
							title="Tagged Database Resources"
							currentValue={resourceKPIs.PercentageTaggedDatabase}
							// previousValue={resourceKPIs.filter(kpi => kpi.resource_group === "Database").untagged_resources}
							format="percentage"
							// showChange={true}
						/>
					</div>

					<div className="flex-shrink-0">
						<KPI
							title="Tagged Networking Resources"
							currentValue={resourceKPIs.PercentageTaggedNetworking}
							// previousValue={resourceKPIs.filter(kpi => kpi.resource_group === "Networking").untagged_resources}
							format="percentage"
							// showChange={true}
						/>
					</div>
				</div>

				<div className="bg-card border border-card-bd pt-4 pb-2 my-2 rounded-card h-screen w-screen flex flex-auto flex-col">
					<UntaggedResourceTable
						data={resourceList}
						toggleRow={toggleRow}
						openRows={openRows}
					/>
				</div>
			</div>
		</Fragment>
	);
};

export default UntaggedResources;
